import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Check, ChevronRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { poleImages } from "@/data/projects";
import PageHeader from "@/components/layout/PageHeader";
import Reveal from "@/components/motion/Reveal";
import CtaBand from "@/components/layout/CtaBand";

const Poles = () => {
  const { t } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
  }, [location.hash]);

  return (
    <>
      <PageHeader chapter={t.polesPage.chapter} title={t.polesPage.title} subtitle={t.polesPage.subtitle} testid="poles-header" />

      <div className="bg-ink">
        {t.poles.map((pole, i) => (
          <section
            key={pole.slug}
            id={pole.slug}
            data-testid={`pole-section-${pole.slug}`}
            className="scroll-mt-24 border-b border-line py-16 lg:py-24"
          >
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className={`grid gap-10 lg:grid-cols-12 lg:items-center ${i % 2 === 1 ? "" : ""}`}>
                <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                  <Reveal>
                    <div className="relative overflow-hidden border border-line group">
                      <img
                        src={poleImages[pole.slug]}
                        alt={pole.title}
                        loading="lazy"
                        className="aspect-[4/3] w-full object-cover grayscale-[35%] transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
                      />
                      <span className="absolute left-5 top-5 bg-ink/80 px-3 py-1.5 font-mono text-xs tracking-[0.25em] text-brand backdrop-blur">
                        {t.polesSection.chapter.split("—")[0].trim()} · {pole.num}
                      </span>
                    </div>
                  </Reveal>
                </div>
                <div className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                  <Reveal>
                    <p className="font-mono text-xs uppercase tracking-[0.25em] text-brand">Pôle {pole.num}</p>
                    <h2 className="mt-3 font-display font-bold uppercase tracking-tight leading-[0.95] text-3xl sm:text-4xl text-cream">
                      {pole.title}
                    </h2>
                    <p className="mt-3 font-display text-lg uppercase tracking-wide text-fog italic">{pole.tagline}</p>
                  </Reveal>
                  <Reveal delay={0.1}>
                    <p className="mt-5 text-base leading-relaxed text-fog">{pole.description}</p>
                  </Reveal>
                  <Reveal delay={0.16}>
                    <h3 className="mt-7 font-mono text-[11px] uppercase tracking-[0.25em] text-ash">{t.polesSection.servicesLabel}</h3>
                    <ul className="mt-4 grid gap-x-8 gap-y-2.5 sm:grid-cols-2">
                      {pole.services.map((s) => (
                        <li key={s} className="flex items-start gap-3 text-sm text-cream/90">
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" /> {s}
                        </li>
                      ))}
                    </ul>
                  </Reveal>
                  {pole.process && (
                    <Reveal delay={0.2}>
                      <h3 className="mt-7 font-mono text-[11px] uppercase tracking-[0.25em] text-ash">{t.polesPage.processLabel}</h3>
                      <div className="mt-4 flex flex-wrap items-center gap-2">
                        {pole.process.map((step, j) => (
                          <span key={step} className="flex items-center gap-2">
                            <span className="border border-brand/50 bg-brand/10 px-3 py-1.5 font-mono text-[11px] uppercase tracking-widest text-brand">
                              {step}
                            </span>
                            {j < pole.process.length - 1 && <ChevronRight className="h-4 w-4 text-ash" />}
                          </span>
                        ))}
                      </div>
                    </Reveal>
                  )}
                  <Reveal delay={0.24}>
                    <Link
                      to="/devis"
                      data-testid={`pole-quote-link-${pole.slug}`}
                      className="group mt-8 inline-flex items-center gap-3 border border-line px-6 py-3.5 font-display text-base font-bold uppercase tracking-[0.12em] text-cream transition-colors hover:border-brand hover:text-brand"
                    >
                      {t.polesPage.cta}
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Reveal>
                </div>
              </div>
            </div>
          </section>
        ))}
      </div>

      <CtaBand />
    </>
  );
};

export default Poles;
